
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Divider,
    Paper,
    Stack,
    Typography,
    Link
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import dayjs from "dayjs";
import "dayjs/locale/ru";

import { sendDataToServer } from "utils/functions";
import EripPayment from "components/EripPayment";
import BeznalPayment from "components/BeznalPayment";

const DRAFT_KEY = "advert_draft";




function PaymentInfo({ cashless, payload, companyDetails, totalPrice }) {
    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Информация об оплате
            </Typography>

            {cashless ? (
                <BeznalPayment
                    data={{
                        ...payload,
                        companyDetails,
                        amount: Number(totalPrice).toFixed(2),
                        cashless: true,
                    }}
                />
            ) : (
                <EripPayment
                    service="adv"
                    summ={Number(totalPrice).toFixed(2)}
                />
            )}

            <Alert severity="info" sx={{ mt: 2 }}>
                {cashless ? (
                    <>
                        После отправки объявления необходимо согласовать
                        безналичную оплату по тел.{" "}
                        <Link
                            href="tel:+375213368388"
                            underline="always"
                            sx={{
                                fontWeight: "bold",
                                color: "#1976d2 !important",
                            }}
                        >
                            8 (02133) 6-83-88
                        </Link>{" "}
                        в рабочее время.
                    </>
                ) : (
                    "После отправки объявления необходимо произвести оплату через ЕРИП. Оплата подтверждается в течение 5 минут, после чего оно будет обработано редактором."
                )}
            </Alert>
        </Box>
    );
}











function loadDraft() {
    try {
        return JSON.parse(
            sessionStorage.getItem(DRAFT_KEY) || "null"
        );
    } catch {
        return null;
    }
}

function InfoRow({ label, children }) {
    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                py: 0.75,
                flexWrap: "wrap",
            }}
        >
            <Typography color="text.secondary">
                {label}
            </Typography>

            <Typography fontWeight={500}>
                {children}
            </Typography>
        </Box>
    );
}

export default function AddPreviewPage() {
    const navigate = useNavigate();

    const [draft] = useState(loadDraft);
    const [sending, setSending] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState("");

    if (!draft?.payload || !draft?.offerAccepted) {
        return (
            <Container maxWidth="md" sx={{ py: 5 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Данные объявления отсутствуют.
                    Вернитесь к заполнению формы.
                </Alert>

                <Button
                    variant="contained"
                    onClick={() => navigate("/add")}
                >
                    Подать объявление
                </Button>
            </Container>
        );
    }

    const {
        name,
        phone,
        companyDetails,
        cashless,
        dateStart,
        dateEnd,
        showTV,
        showInt,
        showPan,
        orderSummary,
        payload,
    } = draft;

    const totalPrice = orderSummary[3];

    const periodDays = dayjs(dateEnd).diff(
        dayjs(dateStart),
        "day"
    ) + 1;

    const places = [
        showTV && "Инфоканал",
        showInt && "Интернет",
        showPan && "Светодиодные панели",
    ].filter(Boolean);

    const handleSend = async () => {
        if (sending || success) {
            return;
        }

        setSending(true);
        setError("");

        try {
            const result = await sendDataToServer(payload);

            if (result?.status !== "OK") {
                setError(
                    result?.error ||
                    result?.status ||
                    "Не удалось отправить объявление"
                );
                return;
            }

            sessionStorage.removeItem(DRAFT_KEY);
            setSuccess(true);
        } catch (err) {
            setError(
                "Ошибка связи с сервером. " +
                "Проверьте, не было ли объявление уже отправлено, " +
                "прежде чем повторять операцию."
            );
        } finally {
            setSending(false);
        }
    };

    return (
        <Container
            maxWidth="md"
            sx={{
                py: { xs: 3, sm: 5 },
                maxWidth: "800px !important",
            }}
        >
            <Paper
                variant="outlined"
                sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: 2,
                }}
            >
                <Typography variant="h4" sx={{ mb: 1 }}>
                    Предпросмотр объявления
                </Typography>

                <Typography
                    color="text.secondary"
                    sx={{ mb: 3 }}
                >
                    Проверьте текст и данные заказа
                    перед окончательной отправкой.
                </Typography>

                {success ? (
                    <Stack spacing={3} alignItems="center">
                        <CheckCircleOutlineIcon
                            color="success"
                            sx={{ fontSize: 64 }}
                        />

                        <Alert
                            severity="success"
                            sx={{ width: "100%" }}
                        >
                            Объявление успешно отправлено.
                        </Alert>


                        <Box sx={{ width: "100%" }}>
                            <PaymentInfo
                                cashless={cashless}
                                payload={payload}
                                companyDetails={companyDetails}
                                totalPrice={totalPrice}
                            />
                        </Box>


                        <Button
                            variant="contained"
                            onClick={() => navigate("/")}
                        >
                            На главную
                        </Button>
                    </Stack>
                ) : (
                    <Stack spacing={3}>
                        {/* Предпросмотр объявления */}
                        <Box>
                            <Typography
                                variant="h6"
                                gutterBottom
                            >
                                Текст объявления
                            </Typography>

                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    bgcolor: "action.hover",
                                }}
                            >
                                <Typography
                                    sx={{
                                        whiteSpace: "pre-wrap",
                                        overflowWrap: "anywhere",
                                        fontSize:"1.5em"
                                    }}
                                >
                                    {payload.text}
                                </Typography>
                            </Paper>
                        </Box>

                        {/* Информация о заказчике */}
                        <Box>
                            <Typography
                                variant="h6"
                                gutterBottom
                            >
                                Заказчик
                            </Typography>

                            <InfoRow
                                label={
                                    cashless
                                        ? "Организация"
                                        : "ФИО"
                                }
                            >
                                {name}
                            </InfoRow>

                            <InfoRow label="Телефон">
                                {phone}
                            </InfoRow>

                            {cashless && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        gap: 2,
                                        mt: 1,
                                    }}
                                >
                                    <Typography
                                        color="text.secondary"
                                        sx={{ flexShrink: 0 }}
                                    >
                                        Реквизиты
                                    </Typography>

                                    <Typography
                                        sx={{
                                            textAlign: "right",
                                            whiteSpace: "pre-wrap",
                                            overflowWrap: "anywhere",
                                            minWidth: 0,
                                        }}
                                    >
                                        {companyDetails}
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        <Divider />

                        {/* Размещение */}
                        <Box>
                            <Typography
                                variant="h6"
                                gutterBottom
                            >
                                Размещение объявления
                            </Typography>

                            <InfoRow label="Начало">
                                {dayjs(dateStart).format(
                                    "DD.MM.YYYY"
                                )}
                            </InfoRow>

                            <InfoRow label="Окончание">
                                {dayjs(dateEnd).format(
                                    "DD.MM.YYYY"
                                )}
                            </InfoRow>

                            <InfoRow label="Длительность">
                                {periodDays} дн.
                            </InfoRow>

                            <InfoRow label="Площадки">
                                {places.join(", ")}
                            </InfoRow>
                        </Box>

                        <Divider />

                        {/* Оплата */}
                        <PaymentInfo
                            cashless={cashless}
                            payload={payload}
                            companyDetails={companyDetails}
                            totalPrice={totalPrice}
                        />

                        {error && (
                            <Alert severity="error">
                                {error}
                            </Alert>
                        )}

                        {/* Кнопки */}
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: {
                                    xs: "column-reverse",
                                    sm: "row",
                                },
                                justifyContent: "space-between",
                                gap: 2,
                                pt: 1,
                            }}
                        >
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate("/add")}
                                disabled={sending}
                            >
                                Вернуться к редактированию
                            </Button>

                            <Button
                                variant="contained"
                                size="large"
                                startIcon={
                                    sending ? (
                                        <CircularProgress
                                            size={18}
                                            color="inherit"
                                        />
                                    ) : (
                                        <SendIcon />
                                    )
                                }
                                onClick={handleSend}
                                disabled={sending}
                            >
                                {sending
                                    ? "Отправка..."
                                    : "Отправить объявление"}
                            </Button>
                        </Box>
                    </Stack>
                )}
            </Paper>
        </Container>
    );
}
